import { GitHubService } from '../services/GitHubService';
import {
  blobForBrowserImageDisplay,
  needsBrowserImageConversion,
  sniffImageMime,
} from '../utils/imageDisplay';
import { mimeForImagePath } from '../utils/itemImages';
import { imageLoadRepoPathsAsync } from '../utils/imageThumbnail';
import { withoutGitBusyOverlay } from '../utils/gitBusy';

/** Blob URL cache for item images in the file view. */
export class AppImageCache {
  private readonly urls = new Map<string, string>();

  revokeAll(): void {
    for (const url of this.urls.values()) URL.revokeObjectURL(url);
    this.urls.clear();
  }

  async getDisplayUrl(
    github: GitHubService,
    repoPath: string,
    fullSize = false,
    bustCache = false,
    mdPath?: string
  ): Promise<string> {
    const cacheKey = fullSize
      ? `full:v5:${mdPath ?? ''}:${repoPath}`
      : `display:v3:${mdPath ?? ''}:${repoPath}`;
    if (bustCache) {
      const old = this.urls.get(cacheKey);
      if (old) URL.revokeObjectURL(old);
      this.urls.delete(cacheKey);
    }
    const cached = this.urls.get(cacheKey);
    if (cached) return cached;

    const pathsToTry = await imageLoadRepoPathsAsync(github, repoPath, !fullSize, mdPath);

    let lastErr: unknown;
    for (const path of pathsToTry) {
      try {
        const { bytes } = await withoutGitBusyOverlay(() => github.readBinary(path));
        const mime = sniffImageMime(bytes) ?? mimeForImagePath(path);
        let blob = new Blob([new Uint8Array(bytes)], { type: mime });
        if (fullSize && needsBrowserImageConversion(mime)) {
          blob = await blobForBrowserImageDisplay(blob);
        }
        const url = URL.createObjectURL(blob);
        this.urls.set(cacheKey, url);
        return url;
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error('Could not load image');
  }
}
